import React from 'react'
import constants from '../../../../config/constants'
import { Formik } from 'formik'
import * as yup from 'yup'
import {
  isValidPersonName,
  toPersonName
} from '../../../../utility/stringUtils'
import { Form, Col } from 'react-bootstrap'
import { VendorUser } from '../../../../models/VendorUser'
import { toast } from 'react-toastify'
import { uploadFile } from '../../../../services/genericService'

interface EditDetailsFormProps {
  profile?: VendorUser
  callback: (
    firstName: string,
    lastName: string,
    avatarUrl?: string,
    currentPass?: string,
    newPass?: string
  ) => void
  isSubmitting: boolean
}

const msg = constants.inlineError
const schema = yup.object({
  firstName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', msg.auth.invalidFirstName, (value) => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', msg.auth.invalidLastName, (value) => {
      return isValidPersonName(value)
    }),
  currentPass: yup.string().when('newPass', {
    is: (val) => {
      return val ? val.length > 0 : 0
    },
    then: yup
      .string()
      .required(msg.fieldRequired.replace('%s', 'Current password'))
  }),
  newPass: yup
    .string()
    .min(constants.passwordMinLength, msg.auth.invalidPassword)
})

const EditDetailsForm: React.FC<EditDetailsFormProps> = ({
  profile,
  callback,
  isSubmitting
}) => {
  return (
    <Formik
      validationSchema={schema}
      onSubmit={(values) => {
        callback(
          values.firstName,
          values.lastName,
          values.avatarUrl,
          values.currentPass,
          values.newPass
        )
      }}
      initialValues={{
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        email: profile?.email || '',
        avatarUrl: profile?.avatarUrl || '',
        currentPass: '',
        newPass: ''
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Row>
            <Form.Group as={Col} md="auto">
              <img
                width="100"
                height="100"
                src={values.avatarUrl || '/profilepic.png'}
                alt="User avatar"
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label htmlFor="edit-profile-avatar">
                Upload avatar
              </Form.Label>
              <Form.File>
                <Form.File.Input
                  className="pb-lg-3"
                  id="edit-profile-avatar"
                  name="avatarUrl"
                  accept="image/*"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const logo = e.target.files![0]
                    toast.info(constants.alertMessage.uploadingImage, {
                      autoClose: false
                    })
                    uploadFile(logo, 'avatars').then((url) => {
                      setFieldValue('avatarUrl', url)
                      toast.dismiss()
                    })
                  }}
                />
              </Form.File>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label htmlFor="edit-profile-first-name">
                First Name
              </Form.Label>
              <Form.Control
                id="edit-profile-first-name"
                type="text"
                name="firstName"
                placeholder="Enter first name"
                value={values.firstName}
                onBlur={handleBlur}
                isInvalid={touched.firstName && !!errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fName = toPersonName(e.target.value)
                  setFieldValue('firstName', fName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label htmlFor="edit-profile-last-name">
                Last Name
              </Form.Label>
              <Form.Control
                id="edit-profile-last-name"
                type="text"
                name="lastName"
                placeholder="Enter last name"
                value={values.lastName}
                onBlur={handleBlur}
                isInvalid={touched.lastName && !!errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const lName = toPersonName(e.target.value)
                  setFieldValue('lastName', lName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-email">Email</Form.Label>
            <Form.Control
              id="edit-profile-email"
              type="email"
              name="email"
              placeholder="Enter email"
              value={values.email}
              readOnly
            />
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-current-password">
              Current Password
            </Form.Label>
            <Form.Control
              id="edit-profile-current-password"
              type="password"
              name="currentPass"
              placeholder="Enter current password"
              value={values.currentPass}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.currentPass && !!errors.currentPass}
            />
            <Form.Control.Feedback type="invalid">
              {errors.currentPass}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label htmlFor="edit-profile-new-password">
              New Password
            </Form.Label>
            <Form.Control
              id="edit-profile-new-password"
              type="password"
              name="newPass"
              placeholder="Enter new password"
              value={values.newPass}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.newPass && !!errors.newPass}
            />
            <Form.Control.Feedback type="invalid">
              {errors.newPass}
            </Form.Control.Feedback>
          </Form.Group>
          <div className="text-center mt-4">
            <button
              className="mochi-button orange"
              type="submit"
              data-testid="edit-profile-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default EditDetailsForm
